import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import GetCookie from '../utils/GetCookie';
import ErrorPopup from '../utils/popup/ErrorPopup';
import ProcessingPopup from '../utils/popup/ProcessingPopup';
import { TopNav, SideNav } from '../iamConsole/navigation/ConsoleNavigation';
import { AdminTopNav, AdminSideNav } from '../administration/administrationNavigation/AdminConsoleNavigation';

const UserGuide = ({ consoleType }) => {
  const navigate = useNavigate();
  const accessToken = GetCookie('access_token');
  
  const [guideContent, setGuideContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableOfContents, setTableOfContents] = useState([]);
  const [activeSection, setActiveSection] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  
  const contentRef = useRef(null);

  useEffect(() => {
    fetchGuideContent();
  }, [consoleType]);

  useEffect(() => {
    if (guideContent) {
      extractTableOfContents(guideContent);
    }
  }, [guideContent]);

  const fetchGuideContent = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await axios.get('/pceapi/general/user-guide-content', {
        params: { console_type: consoleType },
        headers: { Authorization: accessToken }
      });
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        const loginPath = consoleType === 'admin-console' ? '/admin-login' : '/login';
        navigate(loginPath);
        return;
      }
      
      if (data.status === 'failed') {
        setError(data.message || 'Failed to load user guide');
        return;
      }
      
      if (data.status === 'success') {
        setGuideContent(data.data.content);
      }
    } catch (err) {
      console.error('Error fetching user guide:', err);
      setError('Unable to load user guide. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const extractTableOfContents = (content) => {
    const lines = content.split('\n');
    const toc = [];
    
    lines.forEach((line) => {
      const match = line.match(/^(#{1,6})\s*(\d+(?:\.\d+)*)\.\s*(.+)$/);
      
      if (match) {
        const level = match[1].length;
        const number = match[2];
        const title = match[3].trim();
        const id = `section-${number.replace(/\./g, '-')}`;
        
        toc.push({
          level,
          number,
          title,
          id
        });
      }
    });
    
    setTableOfContents(buildTreeStructure(toc));
  };

  const buildTreeStructure = (flatToc) => {
    const tree = [];
    const map = {};
    
    flatToc.forEach(item => {
      const parts = item.number.split('.');
      const parentNumber = parts.slice(0, -1).join('.');
      
      const node = {
        ...item,
        children: [],
        hasChildren: false
      };
      
      map[item.number] = node;
      
      if (parentNumber && map[parentNumber]) {
        map[parentNumber].children.push(node);
        map[parentNumber].hasChildren = true;
      } else {
        tree.push(node);
      }
    });
    
    return tree;
  };

  const toggleSection = (number) => {
    setExpandedSections(prev => ({
      ...prev,
      [number]: !prev[number]
    }));
  };

  const handleRetry = () => {
    setError(null);
    fetchGuideContent();
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  const renderTocItem = (item) => {
    const isExpanded = expandedSections[item.number];
    const hasChildren = item.hasChildren;
    const isActive = activeSection === item.id;

    const handleItemClick = () => {
      scrollToSection(item.id);
      // Opening a parent also reveals its sub-sections
      if (hasChildren && !isExpanded) {
        toggleSection(item.number);
      }
    };

    return (
      <div key={item.id}>
        <div className="d-flex align-items-center my-2">
          {/* Fixed-width slot keeps every title aligned, with or without a chevron */}
          <span
            className="d-inline-flex justify-content-center align-items-center flex-shrink-0 me-1"
            style={{ width: '16px' }}
          >
            {hasChildren && (
              <i
                className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} cursor-pointer ev-xsmall-p-dark`}
                role="button"
                aria-label={isExpanded ? `Collapse ${item.title}` : `Expand ${item.title}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSection(item.number);
                }}
              />
            )}
          </span>
          <span
            onClick={handleItemClick}
            className={`sidelink m-0 p-0 bg-transparent cursor-pointer ${item.level === 1 ? '' : 'ev-fs-12 ev-bold-reg'} ${isActive ? 'active' : ''}`}
          >
            {item.number}. {item.title}
          </span>
        </div>

        {hasChildren && isExpanded && (
          <div className="margin-left-10">
            {item.children.map(child => renderTocItem(child))}
          </div>
        )}
      </div>
    );
  };

  const renderNavigation = () => {
    if (consoleType === 'admin-console') {
      return (
        <>
          <AdminTopNav />
          <AdminSideNav />
        </>
      );
    } else {
      return (
        <>
          <TopNav />
          <SideNav />
        </>
      );
    }
  };

  return (
    <div className="main-window">
      {renderNavigation()}
      
      <div className="container-fluid">
        <div className="row">
          <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            <div className="mx-1 my-3">
              <div className="row">
               
                  <div className="col-3 pe-1">
                     {guideContent && tableOfContents.length > 0 && (
                    <div className="ev-card-2  pe-0">
                      <h5 className="ev-message-card5 ev-btn-large ev-fs-13 w-95 ev-radius-large ev-bold-med margin-bottom-4">Table of Contents</h5>
                      <nav className="vh-75 scrollable-element margin-bottom-4 pe-3">
                        {tableOfContents.map(item => renderTocItem(item))}
                      </nav>
                    </div>
                     )}
                  </div>
               

                <div className="col-9">
                  <div className={` ${guideContent && tableOfContents.length > 0 ? 'ev-card-4 ev-padding-large pe-0' : ''}`} ref={contentRef}>
                    {guideContent ? (
                      <>
                      <h4 className="ev-small-p-blue ev-fs-18 margin-top-1 ev-bold-med">
                       BMS {consoleType === 'admin-console' ? 'Admin' : ' User'}  Guide
                      </h4>           
                      <div className="user-guide-content text-justify vh-75 scrollable-element pe-3">
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          components={{
                            h1: ({node, children, ...props}) => {
                              const text = children.toString();
                              const match = text.match(/^(\d+(?:\.\d+)*)\.\s*(.+)$/);
                              const id = match ? `section-${match[1].replace(/\./g, '-')}` : undefined;
                              return <h1 id={id} className="ev-card-1 ev-radius-small ev-small-p-blue ev-fs-13 ev-bold-med margin-top-2" {...props}>{children}</h1>;
                            },
                            h2: ({node, children, ...props}) => {
                              const text = children.toString();
                              const match = text.match(/^(\d+(?:\.\d+)*)\.\s*(.+)$/);
                              const id = match ? `section-${match[1].replace(/\./g, '-')}` : undefined;
                              return <h2 id={id} className="ev-card-2 ev-radius-small ev-small-p-blue ev-fs-13 ev-bold-med margin-top-2" {...props}>{children}</h2>;
                            },
                            h3: ({node, children, ...props}) => {
                              const text = children.toString();
                              const match = text.match(/^(\d+(?:\.\d+)*)\.\s*(.+)$/);
                              const id = match ? `section-${match[1].replace(/\./g, '-')}` : undefined;
                              return <h3 id={id} className="ev-reg-p-blue ev-bold-med margin-top-1" {...props}>{children}</h3>;
                            },
                            h4: ({node, children, ...props}) => {
                              const text = children.toString();
                              const match = text.match(/^(\d+(?:\.\d+)*)\.\s*(.+)$/);
                              const id = match ? `section-${match[1].replace(/\./g, '-')}` : undefined;
                              return <h4 id={id} className="ev-reg-p-dark ev-fs-12 ev-bold-med margin-top-1" {...props}>{children}</h4>;
                            },
                            h5: ({node, children, ...props}) => {
                              const text = children.toString();
                              const match = text.match(/^(\d+(?:\.\d+)*)\.\s*(.+)$/);
                              const id = match ? `section-${match[1].replace(/\./g, '-')}` : undefined;
                              return <h5 id={id} className="ev-reg-p-dark ev-fs-12 ev-bold-med margin-top-1" {...props}>{children}</h5>;
                            },
                            p: ({node, ...props}) => <p className="ev-small-p-dark m-0" {...props} />,
                            ul: ({node, ...props}) => <ul className="ev-radius-small margin-top-1" {...props} />,
                            ol: ({node, ...props}) => <ol className="ev-radius-small margin-top-1" {...props} />,
                            li: ({node, ...props}) => <li className="ev-small-p-dark margin-left-3 mt-1" {...props} />,
                            table: ({node, ...props}) => (
                              <div className="copilot-table-container">
                                <div className="table-wrapper">
                                  <table className="copilot-custom-table table" {...props} />
                                </div>
                              </div>
                            ),
                          }}
                        >
                          {guideContent}
                        </ReactMarkdown>
                      </div>
                      </>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {error && (
        <ErrorPopup
          onClose={handleRetry}
          message={error}
        />
      )}

      {isLoading && (
        <ProcessingPopup message="Loading user guide" />
      )}
    </div>
  );
};

export default UserGuide;